import { createFileRoute, Navigate, useLocation } from '@tanstack/react-router';
import { Outlet } from '@/lib/router-components';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth.store';
import { useEventBusStore } from '../stores/event-bus.store';
import { useIsAdmin } from '../hooks/useIsAdmin';
import api from '../lib/api';
import { closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { DndContext } from '../lib/dnd-components';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactQuill from '../lib/react-quill-component';
import 'react-quill/dist/quill.snow.css';
import { youtubeUrlToEmbed } from '../lib/youtube-embed';
import { sanitizeHtml } from '../lib/sanitize-html';
import { PlanDto, CreatePlanDto, UpdatePlanDto } from '@prometeus/contracts';
import { AdminWorkspaceDto } from '@prometeus/contracts';
import { CertificationManager } from '@/features/admin/certification/CertificationManager';
import { LoginSlidesManager } from '@/features/admin/login-slides/LoginSlidesManager';
import { getApiErrorMessage } from '@/lib/error-message';
import { AiDashboard, AiTriggersManager, AiGlobalSettings } from '@/features/admin/ai';
import { AdminJobsPanel } from '@/features/admin/jobs';
import { TribunaisManager } from '@/features/admin/tribunais';
import { ADMIN_ACTIVE_TAB_KEY } from '@/features/admin/storageKeys';
import { Link } from '@/lib/router-components';

// Importar DTOs de process-sync (pode precisar recompilar contracts)
type SyncLogDto = {
  id: string;
  startedAt: Date | string;
  finishedAt: Date | string | null;
  status: 'running' | 'completed' | 'failed';
  totalProcesses: number;
  newMovements: number;
  errors: Array<{
    processId?: string;
    processNumber?: string;
    error: string;
    timestamp: Date | string;
  }> | null;
  errorLogs: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: Date | string;
};

// SyncStatsDto type (pode precisar recompilar contracts)

type TabType =
  | 'plans'
  | 'workspaces'
  | 'process-sync'
  | 'banner'
  | 'features'
  | 'login-slides'
  | 'certification'
  | 'bug-reports'
  | 'ai'
  | 'jobs'
  | 'tribunais';

export { CertificationManager };

const ADMIN_AI_SUBTAB_KEY = 'admin-ai-subtab';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

/** Layout: rotas filhas (ex.: `/admin/workspace/$id`) renderizam em `<Outlet />`. */
function AdminLayout() {
  const user = useAuthStore((state) => state.user);
  const { isAdmin, loading } = useIsAdmin();
  const { pathname } = useLocation();

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (loading && isAdmin === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  if (isAdmin === false) {
    return <Navigate to="/workspaces" />;
  }

  /* Rotas filhas (workspace / calibração de tribunal) renderizam em `<Outlet />`. */
  if (pathname.startsWith('/admin/workspace/') || pathname.startsWith('/admin/tribunais/')) {
    return <Outlet />;
  }

  return <AdminDashboardPage />;
}

export function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    if (typeof window === 'undefined') return 'plans';
    const saved = sessionStorage.getItem(ADMIN_ACTIVE_TAB_KEY) as TabType | null;
    const allowed: TabType[] = [
      'plans',
      'workspaces',
      'process-sync',
      'banner',
      'features',
      'login-slides',
      'certification',
      'bug-reports',
      'ai',
      'jobs',
      'tribunais',
    ];
    return saved && allowed.includes(saved) ? saved : 'plans';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(ADMIN_ACTIVE_TAB_KEY, activeTab);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen min-w-0 bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto min-w-0 max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Painel Administrativo</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Gerencie planos, workspaces e sincronização de processos do sistema
          </p>
        </div>

        {/* Tabs — scroll horizontal quando não couber na largura */}
        <div className="mb-6 min-w-0 w-full border-b border-gray-200 dark:border-gray-700">
          <nav
            className="-mb-px flex min-w-0 flex-nowrap gap-8 overflow-x-auto overscroll-x-contain [scrollbar-width:thin] [&>button]:shrink-0"
            role="tablist"
            aria-label="Seções do painel administrativo"
          >
            <button
              onClick={() => setActiveTab('plans')}
              className={`${
                activeTab === 'plans'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Gerenciar Planos
            </button>
            <button
              onClick={() => setActiveTab('workspaces')}
              className={`${
                activeTab === 'workspaces'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Gerenciar Workspaces
            </button>
            <button
              onClick={() => setActiveTab('process-sync')}
              className={`${
                activeTab === 'process-sync'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Sincronização de Processos
            </button>
            <button
              onClick={() => setActiveTab('banner')}
              className={`${
                activeTab === 'banner'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Banner
            </button>
            <button
              onClick={() => setActiveTab('features')}
              className={`${
                activeTab === 'features'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Funcionalidades
            </button>
            <button
              onClick={() => setActiveTab('login-slides')}
              className={`${
                activeTab === 'login-slides'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Slides de Login
            </button>
            <button
              onClick={() => setActiveTab('certification')}
              className={`${
                activeTab === 'certification'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Certificação (Cursos)
            </button>
            <button
              onClick={() => setActiveTab('bug-reports')}
              className={`${
                activeTab === 'bug-reports'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Bug Reports
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`${
                activeTab === 'ai'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Inteligencia Artificial
            </button>
            <button
              onClick={() => setActiveTab('jobs')}
              className={`${
                activeTab === 'jobs'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Filas de Jobs
            </button>
            <button
              onClick={() => setActiveTab('tribunais')}
              className={`${
                activeTab === 'tribunais'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Cadastro de Tribunal
            </button>
          </nav>
        </div>

        {activeTab === 'plans' && <PlansManager />}
        {activeTab === 'workspaces' && <WorkspacesManager />}
        {activeTab === 'process-sync' && <ProcessSyncManager />}
        {activeTab === 'banner' && <BannerManager />}
        {activeTab === 'features' && <FeaturesManager />}
        {activeTab === 'login-slides' && <LoginSlidesManager />}
        {activeTab === 'certification' && <CertificationManager apiBase="/admin/certification" />}
        {activeTab === 'bug-reports' && <BugReportsManager />}
        {activeTab === 'ai' && <AiAdminPanel />}
        {activeTab === 'jobs' && <AdminJobsPanel />}
        {activeTab === 'tribunais' && <TribunaisManager />}
      </div>
    </div>
  );
}

type BugReportAdmin = {
  id: string;
  ticketCode?: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved';
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string | null;
  adminResponse?: string | null;
  screenshotUrl?: string | null;
  reporter?: { name?: string; email?: string };
  emailLogs?: Array<{
    id: string;
    status?: 'queued' | 'sent' | 'failed';
    queuedAt?: string;
    sentAt: string;
    failedAt?: string | null;
    sentByName: string;
    sentByEmail: string;
    responseText: string;
    isResolvedEmail: boolean;
    errorMessage?: string | null;
  }>;
  statusHistory?: Array<{
    id: string;
    fromStatus: 'open' | 'in_progress' | 'resolved';
    toStatus: 'open' | 'in_progress' | 'resolved';
    changedByName: string;
    changedByEmail: string;
    changedAt: string;
    note?: string | null;
  }>;
};

function BugReportsManager() {
  const queryClient = useQueryClient();
  const [filterUser, setFilterUser] = useState('');
  const [filterTitle, setFilterTitle] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [debouncedFilterUser, setDebouncedFilterUser] = useState('');
  const [debouncedFilterTitle, setDebouncedFilterTitle] = useState('');
  const [debouncedFilterCode, setDebouncedFilterCode] = useState('');
  const limit = 5000;
  const [draggingReportId, setDraggingReportId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedFilterUser(filterUser.trim());
      setDebouncedFilterTitle(filterTitle.trim());
      setDebouncedFilterCode(filterCode.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [filterUser, filterTitle, filterCode]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'admin-bug-reports',
      debouncedFilterUser,
      debouncedFilterTitle,
      debouncedFilterCode,
      limit,
    ],
    refetchInterval: 15000,
    queryFn: async () => {
      const response = await api.get('/admin/bug-reports', {
        params: {
          user: debouncedFilterUser || undefined,
          title: debouncedFilterTitle || undefined,
          code: debouncedFilterCode || undefined,
          page: 1,
          limit,
        },
      });
      return response.data?.data ?? response.data;
    },
  });

  const [selected, setSelected] = useState<BugReportAdmin | null>(null);
  const [responseText, setResponseText] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const updateStatusMutation = useMutation({
    mutationFn: async (params: { id: string; status: 'open' | 'in_progress' | 'resolved'; adminResponse?: string }) => {
      const response = await api.patch(`/admin/bug-reports/${params.id}/status`, {
        status: params.status,
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-bug-reports'] });
      setSelected(null);
      setResponseText('');
      setModalError(null);
    },
    onError: (err: any) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível atualizar o status do bug.';
      setModalError(Array.isArray(message) ? message.join(', ') : message);
    },
  });

  const replyMutation = useMutation({
    mutationFn: async ({ id, adminResponse }: { id: string; adminResponse: string }) => {
      const response = await api.post(`/admin/bug-reports/${id}/reply`, {
        adminResponse,
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-bug-reports'] });
      if (selected?.id) {
        const response = await api.get(`/admin/bug-reports/${selected.id}`);
        const full = (response.data?.data ?? response.data) as BugReportAdmin;
        setSelected(full);
      }
      setModalError(null);
    },
    onError: (err: any) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível enviar a resposta.';
      setModalError(Array.isArray(message) ? message.join(', ') : message);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async ({ id, adminResponse }: { id: string; adminResponse: string }) => {
      const response = await api.post(`/admin/bug-reports/${id}/resolve`, {
        adminResponse,
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-bug-reports'] });
      if (selected?.id) {
        const response = await api.get(`/admin/bug-reports/${selected.id}`);
        const full = (response.data?.data ?? response.data) as BugReportAdmin;
        setSelected(full);
      }
      setModalError(null);
    },
    onError: (err: any) => {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível resolver o bug.';
      setModalError(Array.isArray(message) ? message.join(', ') : message);
    },
  });

  const reportsContainer = (data as any)?.data && !Array.isArray((data as any)?.data)
    ? (data as any).data
    : data;
  const reports: BugReportAdmin[] = Array.isArray((reportsContainer as any)?.data)
    ? (reportsContainer as any).data
    : Array.isArray(reportsContainer)
      ? reportsContainer
      : [];
  const total = Number((reportsContainer as any)?.total || reports.length || 0);

  const openDetails = async (report: BugReportAdmin) => {
    setModalError(null);
    setResponseText(report.adminResponse || '');
    setIsLoadingDetails(true);
    try {
      const response = await api.get(`/admin/bug-reports/${report.id}`);
      const full = (response.data?.data ?? response.data) as BugReportAdmin;
      setSelected({ ...report, ...full });
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível carregar os detalhes do bug.';
      setModalError(Array.isArray(message) ? message.join(', ') : message);
      setSelected(report);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const formatStatus = (status: BugReportAdmin['status']) => {
    switch (status) {
      case 'open':
        return 'Aberto';
      case 'in_progress':
        return 'Em andamento';
      case 'resolved':
        return 'Resolvido';
      default:
        return status;
    }
  };

  const statusBadgeClasses = (status: BugReportAdmin['status']) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('pt-BR');
  };

  const getReportsByStatus = (status: BugReportAdmin['status']) =>
    reports
      .filter((r) => r.status === status)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );

  const handleDrop = (targetStatus: BugReportAdmin['status']) => {
    if (!draggingReportId) return;
    const report = reports.find((r) => r.id === draggingReportId);
    if (!report || report.status === targetStatus) {
      setDraggingReportId(null);
      return;
    }
    updateStatusMutation.mutate({
      id: report.id,
      status: targetStatus,
    });
    setDraggingReportId(null);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Bug Reports
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Acompanhe problemas reportados pelos usuários e registre a resolução.
          </p>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <input
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          placeholder="Filtrar por usuário (nome/e-mail)"
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
        <input
          value={filterTitle}
          onChange={(e) => setFilterTitle(e.target.value)}
          placeholder="Filtrar por título do bug"
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
        <input
          value={filterCode}
          onChange={(e) => setFilterCode(e.target.value)}
          placeholder="Filtrar por código (ATD-...)"
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />
      </div>

      <div className="mb-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          Total de bugs: <strong>{total}</strong>
        </span>
        <span>Atualização automática a cada 15s</span>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Carregando bug reports...
        </p>
      ) : reports.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Nenhum bug report encontrado.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {(['open', 'in_progress', 'resolved'] as const).map((status) => {
            const items = getReportsByStatus(status);
            return (
              <div
                key={status}
                className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950/60"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(status)}
              >
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {formatStatus(status)}
                  </h3>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-300">
                    {items.length}
                  </span>
                </div>
                <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                  {items.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Sem bugs neste status.
                    </p>
                  ) : (
                    items.map((report) => (
                      <button
                        key={report.id}
                        type="button"
                        draggable
                        onDragStart={() => setDraggingReportId(report.id)}
                        onDragEnd={() => setDraggingReportId(null)}
                        onClick={() => openDetails(report)}
                        className="w-full rounded-md border border-gray-200 bg-white p-3 text-left transition hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:hover:border-indigo-500"
                      >
                        <div className="line-clamp-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {report.title || 'Sem título'}
                        </div>
                        <div className="mt-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-300">
                          {report.ticketCode || 'Sem código'}
                        </div>
                        <div className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">
                          {report.description}
                        </div>
                        <div className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                          {report.reporter?.email || 'Usuário não identificado'}
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                          {formatDateTime(report.createdAt)}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
          <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {selected.title || 'Bug report'}
                </h3>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold ${statusBadgeClasses(
                      selected.status,
                    )}`}
                  >
                    {formatStatus(selected.status)}
                  </span>
                  <span>Aberto em {formatDateTime(selected.createdAt)}</span>
                  {selected.resolvedAt && (
                    <span>Resolvido em {formatDateTime(selected.resolvedAt)}</span>
                  )}
                  {selected.reporter?.email && (
                    <span>Reporter: {selected.reporter.email}</span>
                  )}
                  {selected.ticketCode && <span>Código: {selected.ticketCode}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelected(null);
                  setResponseText('');
                  setModalError(null);
                }}
                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>

            {isLoadingDetails ? (
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                Carregando detalhes do bug...
              </p>
            ) : (
              <>
                <div className="mb-4 grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Descrição do problema
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                      {selected.description}
                    </div>
                  </div>

                  {selected.screenshotUrl && (
                    <div className="md:col-span-2">
                      <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Captura de tela
                      </h4>
                      <div className="overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900">
                        <img
                          src={selected.screenshotUrl}
                          alt="Captura de tela do bug"
                          className="max-h-80 w-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Resposta ao usuário (enviada por e-mail)
                  </label>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    className="min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Explique o que foi feito para resolver o problema ou como o usuário pode proceder."
                  />
                </div>

                <div className="mt-5">
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Histórico de envios por e-mail
                  </h4>
                  <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                    {selected.emailLogs && selected.emailLogs.length > 0 ? (
                      <div className="space-y-2">
                        {selected.emailLogs.map((log) => (
                          <div
                            key={log.id}
                            className="rounded border border-gray-200 bg-white p-2 text-xs dark:border-gray-700 dark:bg-gray-800"
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {log.isResolvedEmail ? 'Resolução' : 'Resposta'} -{' '}
                              {log.status === 'sent'
                                ? 'Enviado'
                                : log.status === 'failed'
                                  ? 'Falhou'
                                  : 'Em fila'}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {formatDateTime(log.sentAt || log.queuedAt)} - {log.sentByName} ({log.sentByEmail})
                            </div>
                            {log.errorMessage && (
                              <div className="mt-1 text-red-500">
                                Erro: {log.errorMessage}
                              </div>
                            )}
                            <div className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                              {log.responseText}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Nenhum envio registrado para este bug.
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5">
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Histórico de status
                  </h4>
                  <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                    {selected.statusHistory && selected.statusHistory.length > 0 ? (
                      <div className="space-y-2">
                        {selected.statusHistory.map((h) => (
                          <div
                            key={h.id}
                            className="rounded border border-gray-200 bg-white p-2 text-xs dark:border-gray-700 dark:bg-gray-800"
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {formatStatus(h.fromStatus)} {'->'} {formatStatus(h.toStatus)}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400">
                              {formatDateTime(h.changedAt)} - {h.changedByName} ({h.changedByEmail})
                            </div>
                            {h.note && (
                              <div className="mt-1 text-gray-700 dark:text-gray-300">
                                {h.note}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Nenhuma mudança de status registrada.
                      </p>
                    )}
                  </div>
                </div>

                {modalError && (
                  <p className="mt-2 text-sm text-red-500">
                    {modalError}
                  </p>
                )}

                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    A ação de resolução envia a resposta por e-mail e marca o ticket como resolvido.
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        replyMutation.mutate({
                          id: selected.id,
                          adminResponse: responseText,
                        })
                      }
                      disabled={replyMutation.isPending || !responseText.trim()}
                      className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {replyMutation.isPending ? 'Enviando...' : 'Enviar resposta'}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        resolveMutation.mutate({
                          id: selected.id,
                          adminResponse: responseText,
                        })
                      }
                      disabled={
                        resolveMutation.isPending ||
                        !responseText.trim()
                      }
                      className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    >
                      {resolveMutation.isPending ? 'Salvando...' : 'Marcar como resolvido'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

type AiSubTab = 'dashboard' | 'triggers' | 'settings';

function AiAdminPanel() {
  const [subTab, setSubTab] = useState<AiSubTab>(() => {
    if (typeof window === 'undefined') return 'dashboard';
    const saved = sessionStorage.getItem(ADMIN_AI_SUBTAB_KEY) as AiSubTab | null;
    return saved === 'dashboard' || saved === 'triggers' || saved === 'settings'
      ? saved
      : 'dashboard';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(ADMIN_AI_SUBTAB_KEY, subTab);
    }
  }, [subTab]);

  return (
    <div className="min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="min-w-0 border-b border-gray-200 dark:border-gray-700">
        <nav
          className="-mb-px flex min-w-0 flex-nowrap overflow-x-auto overscroll-x-contain [scrollbar-width:thin] [&>button]:shrink-0"
          role="tablist"
          aria-label="Seções de IA"
        >
          {([
            { key: 'dashboard', label: 'Dashboard' },
            { key: 'triggers', label: 'Gatilhos' },
            { key: 'settings', label: 'Configuracoes' },
          ] as { key: AiSubTab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSubTab(key)}
              className={`whitespace-nowrap py-4 px-6 text-sm font-medium border-b-2 ${
                subTab === key
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {subTab === 'dashboard' && <AiDashboard />}
        {subTab === 'triggers' && <AiTriggersManager />}
        {subTab === 'settings' && <AiGlobalSettings />}
      </div>
    </div>
  );
}

// Helpers de formatação
const formatPrice = (cents: number | null | undefined, currency: string = 'brl') => {
  if (cents === null || cents === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
};

const formatBillingCycle = (cycle: string | null | undefined) => {
  const labels: Record<string, string> = {
    monthly: 'Mensal',
    quarterly: 'Trimestral',
    semiannual: 'Semestral',
    annual: 'Anual',
  };
  return cycle ? labels[cycle] || cycle : '-';
};

const formatStorage = (bytes: number | undefined) => {
  if (!bytes) return '-';
  return `${(bytes / 1024 / 1024 / 1024).toFixed(0)} GB`;
};

function PlansManager() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanDto | null>(null);

  const { data: plans, isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: async () => {
      const response = await api.get('/admin/plans');
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreatePlanDto) => {
      const response = await api.post('/admin/plans', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePlanDto }) => {
      const response = await api.put(`/admin/plans/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setEditingPlan(null);
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.patch(`/admin/plans/${id}/activate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.patch(`/admin/plans/${id}/deactivate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
    },
  });

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Planos</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Criar Plano
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Ciclo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Preço
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Créditos IA
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Limites
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Padrão
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {(Array.isArray(plans) ? plans : [])?.map((plan: PlanDto) => (
              <tr key={plan.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                  {plan.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      plan.isFree
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                        : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    }`}
                  >
                    {plan.isFree ? '🆓 Gratuito' : '💳 Pago'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {plan.isFree ? (
                    '-'
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                      {formatBillingCycle(plan.billingCycle)}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 font-medium">
                  {plan.isFree ? '-' : formatPrice(plan.price, plan.currency)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                  <span title="Renovação mensal dos créditos">
                    {plan.aiCreditsPerPeriod ?? 3}/mês
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  <div className="space-y-1">
                    <div>{plan.maxClients} clientes</div>
                    <div>{plan.maxProcesses} processos</div>
                    <div>{plan.maxUsers} usuários</div>
                    <div>{plan.limiteTermos ?? 10} termos</div>
                    {plan.maxStorageBytes && (
                      <div className="text-xs text-gray-400">{formatStorage(plan.maxStorageBytes)}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      plan.isActive
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}
                  >
                    {plan.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {plan.isDefault && (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                      Padrão
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingPlan(plan)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      title="Editar"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {plan.isActive ? (
                      <button
                        onClick={() => {
                          if (confirm('Tem certeza que deseja desativar este plano?')) {
                            deactivateMutation.mutate(plan.id);
                          }
                        }}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        disabled={plan.isDefault}
                        title={plan.isDefault ? 'Não é possível desativar o plano padrão' : 'Desativar'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={() => activateMutation.mutate(plan.id)}
                        className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                        title="Ativar"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <PlanModal
          onClose={() => setShowCreateModal(false)}
          onSave={(data) => createMutation.mutate(data as CreatePlanDto)}
        />
      )}

      {editingPlan && (
        <PlanModal
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSave={(data) => updateMutation.mutate({ id: editingPlan.id, data: data as UpdatePlanDto })}
        />
      )}
    </div>
  );
}

function PlanModal({
  plan,
  onClose,
  onSave,
}: {
  plan?: PlanDto;
  onClose: () => void;
  onSave: (data: CreatePlanDto | UpdatePlanDto) => void;
}) {
  const [name, setName] = useState(plan?.name || '');
  const [description, setDescription] = useState(plan?.description || '');
  const [maxClients, setMaxClients] = useState(plan?.maxClients || 1);
  const [maxProcesses, setMaxProcesses] = useState(plan?.maxProcesses || 1);
  const [maxUsers, setMaxUsers] = useState(plan?.maxUsers || 1);
  const [limiteTermos, setLimiteTermos] = useState(plan?.limiteTermos ?? 10);
  const [maxStorageGB, setMaxStorageGB] = useState(
    plan?.maxStorageBytes ? Math.round(plan.maxStorageBytes / 1024 / 1024 / 1024) : 1
  );
  const [isDefault, setIsDefault] = useState(plan?.isDefault || false);
  const [isFree, setIsFree] = useState(plan?.isFree !== undefined ? plan.isFree : true);
  const [stripePriceId, setStripePriceId] = useState(plan?.stripePriceId || '');
  const [stripeProductId, setStripeProductId] = useState(plan?.stripeProductId || '');
  const [billingCycle, setBillingCycle] = useState<
    'monthly' | 'quarterly' | 'semiannual' | 'annual' | ''
  >(plan?.billingCycle || '');
  const [price, setPrice] = useState(plan?.price ? (plan.price / 100).toFixed(2) : '');
  const [currency, setCurrency] = useState(plan?.currency || 'brl');
  const [aiCreditsPerPeriod, setAiCreditsPerPeriod] = useState(
    plan?.aiCreditsPerPeriod ?? 3,
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação para planos pagos
    if (!isFree) {
      if (!stripePriceId.trim() || !price || !billingCycle) {
        alert('Planos pagos devem ter Stripe Price ID, Preço e Ciclo de Cobrança');
        return;
      }
    }

    const submitData: CreatePlanDto | UpdatePlanDto = {
      name,
      description: description || undefined,
      maxClients,
      maxProcesses,
      maxUsers,
      limiteTermos,
      aiCreditsPerPeriod,
      maxStorageBytes: maxStorageGB * 1024 * 1024 * 1024,
      isDefault: isDefault || undefined,
      isFree,
      stripePriceId: isFree ? null : (stripePriceId.trim() || null),
      stripeProductId: isFree ? null : (stripeProductId.trim() || null),
      billingCycle: isFree ? null : (billingCycle || null),
      price: isFree ? null : (price ? Math.round(parseFloat(price) * 100) : null),
      currency: isFree ? undefined : currency,
    };

    onSave(submitData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 z-10 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {plan ? 'Editar Plano' : 'Criar Plano'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome e Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Nome <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Descrição
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            {/* Tipo de Plano */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Plano <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="planType"
                    checked={isFree}
                    onChange={() => setIsFree(true)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Plano Gratuito</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="planType"
                    checked={!isFree}
                    onChange={() => setIsFree(false)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Plano Pago</span>
                </label>
              </div>
            </div>

            {/* Limites */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Limites</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Clientes <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={maxClients}
                    onChange={(e) => setMaxClients(parseInt(e.target.value) || 1)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Processos <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={maxProcesses}
                    onChange={(e) => setMaxProcesses(parseInt(e.target.value) || 1)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Usuários <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={maxUsers}
                    onChange={(e) => setMaxUsers(parseInt(e.target.value) || 1)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Max Termos <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={limiteTermos}
                    onChange={(e) => setLimiteTermos(parseInt(e.target.value, 10) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Armazenamento (GB) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={maxStorageGB}
                    onChange={(e) => setMaxStorageGB(parseInt(e.target.value) || 1)}
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Créditos de IA */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Inteligência Artificial
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Créditos de IA por período <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min={0}
                    value={aiCreditsPerPeriod}
                    onChange={(e) =>
                      setAiCreditsPerPeriod(parseInt(e.target.value, 10) || 0)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Renovação: mensal (alinhado à cobrança / fatura quando houver assinatura Stripe).
                  </p>
                </div>
              </div>
            </div>

            {/* Configuração Stripe - Apenas para planos pagos */}
            {!isFree && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Configuração Stripe
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stripe Price ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required={!isFree}
                      value={stripePriceId}
                      onChange={(e) => setStripePriceId(e.target.value)}
                      placeholder="price_xxxxx"
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      ID do preço criado no Stripe Dashboard
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stripe Product ID
                    </label>
                    <input
                      type="text"
                      value={stripeProductId}
                      onChange={(e) => setStripeProductId(e.target.value)}
                      placeholder="prod_xxxxx"
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      ID do produto criado no Stripe Dashboard (opcional)
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Ciclo de Cobrança <span className="text-red-500">*</span>
                      </label>
                      <select
                        required={!isFree}
                        value={billingCycle}
                        onChange={(e) =>
                          setBillingCycle(
                            e.target.value as
                              | ''
                              | NonNullable<NonNullable<CreatePlanDto['billingCycle']>>
                          )
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">Selecione...</option>
                        <option value="monthly">Mensal</option>
                        <option value="quarterly">Trimestral</option>
                        <option value="semiannual">Semestral</option>
                        <option value="annual">Anual</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Preço (R$) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        required={!isFree}
                        min="0"
                        step="0.01"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="99.00"
                        className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Valor em reais (será convertido para centavos)
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Moeda
                    </label>
                    <input
                      type="text"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      placeholder="brl"
                      className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Código da moeda (padrão: brl)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Plano Padrão */}
            <div className="flex items-center border-t border-gray-200 dark:border-gray-700 pt-4">
              <input
                type="checkbox"
                id="isDefault"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">
                Plano Padrão
              </label>
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function WorkspacesManager() {
  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['admin-workspaces'],
    queryFn: async () => {
      const response = await api.get('/admin/workspaces');
      const payload = response.data?.data;
      if (Array.isArray(payload)) return payload;
      return payload?.items || [];
    },
  });

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Workspaces</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Criador
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email do Criador
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plano Ativo
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(Array.isArray(workspaces) ? workspaces : [])?.map((workspace: AdminWorkspaceDto) => (
              <tr key={workspace.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {workspace.name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {workspace.creator?.name || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {workspace.creator?.email || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {workspace.plan?.name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <Link
                    to="/admin/workspace/$workspaceId"
                    params={{ workspaceId: workspace.id }}
                    className="inline-flex text-indigo-600 hover:text-indigo-900"
                    title="Administrar workspace"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function ProcessSyncManager() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState<SyncLogDto | null>(null);
  const pageSize = 20;

  const { data: logsData, isLoading } = useQuery({
    queryKey: ['admin-process-sync-logs', page],
    queryFn: async () => {
      const response = await api.get(`/admin/process-sync/logs?page=${page}&size=${pageSize}`);
      return response.data;
    },
  });

  const { data: statsData } = useQuery({
    queryKey: ['admin-process-sync-stats'],
    queryFn: async () => {
      const response = await api.get('/admin/process-sync/stats');
      console.log('[ProcessSyncManager] Stats response completa:', response.data);
      // A resposta pode vir como { data: { data: stats } } ou { data: stats }
      // Se vier duplamente envolvida: response.data.data.data
      // Se vier simplesmente envolvida: response.data.data
      if (response.data?.data?.data) {
        console.log('[ProcessSyncManager] Stats (formato triplo):', response.data.data.data);
        return response.data.data.data;
      }
      if (response.data?.data) {
        console.log('[ProcessSyncManager] Stats (formato duplo):', response.data.data);
        return response.data.data;
      }
      console.log('[ProcessSyncManager] Stats (formato simples):', response.data);
      return response.data;
    },
  });
  
  // Extrair stats do formato correto
  const stats = statsData?.data || statsData;
  console.log('[ProcessSyncManager] Stats final:', stats);

  const triggerSyncMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/process-sync/trigger');
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-process-sync-logs'] });
      queryClient.invalidateQueries({ queryKey: ['admin-process-sync-stats'] });
      alert('Sincronização iniciada com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao iniciar sincronização:', error);
      alert(getApiErrorMessage(error, 'Erro ao iniciar sincronização'));
    },
  });

  // Garantir que logs seja sempre um array
  // A resposta da API vem como { data: { data: [...], total, page, size, totalPages } }
  const logs: SyncLogDto[] = (() => {
    if (!logsData) return [];
    
    // Se logsData.data.data é um array (resposta envolvida por interceptor)
    if (logsData.data && logsData.data.data && Array.isArray(logsData.data.data)) {
      console.log('[ProcessSyncManager] Logs encontrados (formato duplo):', logsData.data.data.length);
      return logsData.data.data;
    }
    
    // Se logsData.data é um array (resposta direta do service)
    if (logsData.data && Array.isArray(logsData.data)) {
      console.log('[ProcessSyncManager] Logs encontrados (formato simples):', logsData.data.length);
      return logsData.data;
    }
    
    // Fallback: se logsData é diretamente um array
    if (Array.isArray(logsData)) {
      console.log('[ProcessSyncManager] Logs encontrados (array direto):', logsData.length);
      return logsData;
    }
    
    // Default: array vazio
    console.warn('[ProcessSyncManager] Nenhum formato de logs reconhecido:', logsData);
    return [];
  })();
  
  // Acessar totalPages do objeto correto
  const totalPages = logsData?.data?.totalPages || logsData?.totalPages || 0;
  
  console.log('[ProcessSyncManager] Logs extraídos:', logs.length, 'Total pages:', totalPages);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      running: { label: 'Em Execução', className: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Concluído', className: 'bg-green-100 text-green-800' },
      failed: { label: 'Falhou', className: 'bg-red-100 text-red-800' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  if (isLoading && !logsData) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Card */}
      {stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estatísticas</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-gray-500">Total de Sincronizações</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSyncs ?? 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Bem-sucedidas</p>
              <p className="text-2xl font-bold text-green-600">{stats.successfulSyncs ?? 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Falhas</p>
              <p className="text-2xl font-bold text-red-600">{stats.failedSyncs ?? 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total de Movimentações</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalMovements ?? 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Última Sincronização</p>
              <p className="text-sm font-medium text-gray-900">
                {stats.lastSyncAt ? formatDate(stats.lastSyncAt) : 'Nunca'}
              </p>
            </div>
          </div>
        </div>
      )}
      {!stats && (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-500">Carregando estatísticas...</p>
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Logs de Sincronização</h2>
          <button
            onClick={() => triggerSyncMutation.mutate()}
            disabled={triggerSyncMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {triggerSyncMutation.isPending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Iniciando...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sincronizar Agora
              </>
            )}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Início
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fim
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Processos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Novas Movimentações
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Erros
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!Array.isArray(logs) || logs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    {!Array.isArray(logs) ? 'Erro ao carregar logs' : 'Nenhum log de sincronização encontrado'}
                  </td>
                </tr>
              ) : (
                logs.map((log: SyncLogDto) => (
                <tr key={log.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(log.startedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(log.finishedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(log.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.totalProcesses}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.newMovements}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.errors && log.errors.length > 0 ? (
                      <span className="text-red-600">{log.errors.length}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Ver Detalhes
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Página {page + 1} de {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <LogDetailsModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}

function LogDetailsModal({
  log,
  onClose,
}: {
  log: SyncLogDto;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 z-10 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Detalhes da Sincronização</h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-500">ID</p>
              <p className="text-sm text-gray-900">{log.id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Início</p>
              <p className="text-sm text-gray-900">{new Date(log.startedAt).toLocaleString('pt-BR')}</p>
            </div>
            {log.finishedAt && (
              <div>
                <p className="text-sm font-medium text-gray-500">Fim</p>
                <p className="text-sm text-gray-900">{new Date(log.finishedAt).toLocaleString('pt-BR')}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="text-sm text-gray-900">{log.status}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total de Processos</p>
              <p className="text-sm text-gray-900">{log.totalProcesses}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Novas Movimentações</p>
              <p className="text-sm text-gray-900">{log.newMovements}</p>
            </div>
            {log.errors && log.errors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-500">Erros</p>
                <div className="mt-2 space-y-2">
                  {log.errors.map((error, idx: number) => (
                    <div key={idx} className="bg-red-50 border border-red-200 rounded p-3">
                      <p className="text-sm text-red-800">
                        <strong>Processo:</strong> {error.processNumber || error.processId || 'Desconhecido'}
                      </p>
                      <p className="text-sm text-red-600 mt-1">{error.error}</p>
                      <p className="text-xs text-red-500 mt-1">
                        {new Date(error.timestamp).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {log.errorLogs && (
              <div>
                <p className="text-sm font-medium text-gray-500">Logs de Erro</p>
                <pre className="mt-2 text-xs bg-gray-50 p-3 rounded overflow-auto">
                  {log.errorLogs}
                </pre>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BannerManager() {
  const queryClient = useQueryClient();
  const fileInputRef1 = useRef<HTMLInputElement>(null);
  const fileInputRef2 = useRef<HTMLInputElement>(null);
  const fileInputRef3 = useRef<HTMLInputElement>(null);
  const [imagePreview1, setImagePreview1] = useState<string | null>(null);
  const [imagePreview2, setImagePreview2] = useState<string | null>(null);
  const [imagePreview3, setImagePreview3] = useState<string | null>(null);
  const [imageFile1, setImageFile1] = useState<File | null>(null);
  const [imageFile2, setImageFile2] = useState<File | null>(null);
  const [imageFile3, setImageFile3] = useState<File | null>(null);
  const [link1, setLink1] = useState<string>('');
  const [link2, setLink2] = useState<string>('');
  const [link3, setLink3] = useState<string>('');

  const { data: bannersData, isLoading } = useQuery({
    queryKey: ['admin-banners'],
    queryFn: async () => {
      const response = await api.get('/admin/workspaces/banners');
      return response.data?.data || response.data;
    },
  });

  const uploadMutation1 = useMutation({
    mutationFn: async ({ file, link }: { file: File; link?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (link) {
        formData.append('link', link);
      }
      const response = await api.post('/admin/workspaces/banner/1', formData);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      setImagePreview1(null);
      setImageFile1(null);
      setLink1('');
      alert('Banner 1 atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao fazer upload do banner 1:', error);
      alert(getApiErrorMessage(error, 'Erro ao fazer upload do banner 1'));
    },
  });

  const uploadMutation2 = useMutation({
    mutationFn: async ({ file, link }: { file: File; link?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (link) {
        formData.append('link', link);
      }
      const response = await api.post('/admin/workspaces/banner/2', formData);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      setImagePreview2(null);
      setImageFile2(null);
      setLink2('');
      alert('Banner 2 atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao fazer upload do banner 2:', error);
      alert(getApiErrorMessage(error, 'Erro ao fazer upload do banner 2'));
    },
  });

  const uploadMutation3 = useMutation({
    mutationFn: async ({ file, link }: { file: File; link?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (link) {
        formData.append('link', link);
      }
      const response = await api.post('/admin/workspaces/banner/3', formData);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      setImagePreview3(null);
      setImageFile3(null);
      setLink3('');
      alert('Banner 3 atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao fazer upload do banner 3:', error);
      alert(getApiErrorMessage(error, 'Erro ao fazer upload do banner 3'));
    },
  });

  const updateLinkMutation1 = useMutation({
    mutationFn: async (link: string | null) => {
      await api.put('/admin/workspaces/banner/1/link', { link });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      alert('Link do banner 1 atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao atualizar link do banner 1:', error);
      alert(getApiErrorMessage(error, 'Erro ao atualizar link do banner 1'));
    },
  });

  const updateLinkMutation2 = useMutation({
    mutationFn: async (link: string | null) => {
      await api.put('/admin/workspaces/banner/2/link', { link });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      alert('Link do banner 2 atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao atualizar link do banner 2:', error);
      alert(getApiErrorMessage(error, 'Erro ao atualizar link do banner 2'));
    },
  });

  const updateLinkMutation3 = useMutation({
    mutationFn: async (link: string | null) => {
      await api.put('/admin/workspaces/banner/3/link', { link });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      alert('Link do banner 3 atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao atualizar link do banner 3:', error);
      alert(getApiErrorMessage(error, 'Erro ao atualizar link do banner 3'));
    },
  });

  const deleteMutation1 = useMutation({
    mutationFn: async () => {
      await api.delete('/admin/workspaces/banner/1');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      alert('Banner 1 removido com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao remover banner 1:', error);
      alert(getApiErrorMessage(error, 'Erro ao remover banner 1'));
    },
  });

  const deleteMutation2 = useMutation({
    mutationFn: async () => {
      await api.delete('/admin/workspaces/banner/2');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      alert('Banner 2 removido com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao remover banner 2:', error);
      alert(getApiErrorMessage(error, 'Erro ao remover banner 2'));
    },
  });

  const deleteMutation3 = useMutation({
    mutationFn: async () => {
      await api.delete('/admin/workspaces/banner/3');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-banners'] });
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      alert('Banner 3 removido com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('Erro ao remover banner 3:', error);
      alert(getApiErrorMessage(error, 'Erro ao remover banner 3'));
    },
  });

  const handleImageChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile1(file);
      const preview = URL.createObjectURL(file);
      setImagePreview1(preview);
    }
  };

  const handleImageChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile2(file);
      const preview = URL.createObjectURL(file);
      setImagePreview2(preview);
    }
  };

  const handleImageChange3 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile3(file);
      const preview = URL.createObjectURL(file);
      setImagePreview3(preview);
    }
  };

  const handleUpload1 = () => {
    if (imageFile1) {
      uploadMutation1.mutate({ file: imageFile1, link: link1 || undefined });
    }
  };

  const handleUpload2 = () => {
    if (imageFile2) {
      uploadMutation2.mutate({ file: imageFile2, link: link2 || undefined });
    }
  };

  const handleUpload3 = () => {
    if (imageFile3) {
      uploadMutation3.mutate({ file: imageFile3, link: link3 || undefined });
    }
  };

  const handleUpdateLink1 = () => {
    updateLinkMutation1.mutate(link1 || null);
  };

  const handleUpdateLink2 = () => {
    updateLinkMutation2.mutate(link2 || null);
  };

  const handleUpdateLink3 = () => {
    updateLinkMutation3.mutate(link3 || null);
  };

  const handleDelete1 = () => {
    if (confirm('Tem certeza que deseja remover o banner 1?')) {
      deleteMutation1.mutate();
    }
  };

  const handleDelete2 = () => {
    if (confirm('Tem certeza que deseja remover o banner 2?')) {
      deleteMutation2.mutate();
    }
  };

  const handleDelete3 = () => {
    if (confirm('Tem certeza que deseja remover o banner 3?')) {
      deleteMutation3.mutate();
    }
  };

  const currentBanner1 = bannersData?.banner1 || null;
  const currentBanner2 = bannersData?.banner2 || null;
  const currentBanner3 = bannersData?.banner3 || null;

  // Inicializar links quando os dados carregarem
  useEffect(() => {
    if (currentBanner1?.link && !imagePreview1) {
      setLink1(currentBanner1.link);
    }
    if (currentBanner2?.link && !imagePreview2) {
      setLink2(currentBanner2.link);
    }
    if (currentBanner3?.link && !imagePreview3) {
      setLink3(currentBanner3.link);
    }
  }, [currentBanner1?.link, currentBanner2?.link, currentBanner3?.link, imagePreview1, imagePreview2, imagePreview3]);

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  const renderBannerSection = (
    bannerNumber: 1 | 2 | 3,
    currentBanner: { bannerUrl: string; link: string | null } | null,
    imagePreview: string | null,
    imageFile: File | null,
    link: string,
    setLink: (link: string) => void,
    fileInputRef: React.RefObject<HTMLInputElement>,
    handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    handleUpload: () => void,
    handleUpdateLink: () => void,
    handleDelete: () => void,
    uploadPending: boolean,
    updateLinkPending: boolean,
    deletePending: boolean
  ) => {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Banner {bannerNumber}</h3>
        
        {/* Preview do banner atual */}
        {currentBanner && (
          <div className="mb-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-3" style={{ maxWidth: '400px' }}>
              <img
                src={currentBanner.bannerUrl}
                alt={`Banner ${bannerNumber} atual`}
                className="w-full h-auto"
                style={{ maxHeight: '300px', objectFit: 'contain' }}
              />
            </div>
            {currentBanner.link && (
              <p className="text-sm text-gray-600">
                Link atual: <a href={currentBanner.link} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{currentBanner.link}</a>
              </p>
            )}
          </div>
        )}

        {/* Upload de novo banner */}
        <div className="mb-4">
          {imagePreview ? (
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg overflow-hidden" style={{ maxWidth: '400px' }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-auto"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                />
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link do Banner (opcional)
                  </label>
                  <input
                    type="url"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder="https://exemplo.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleUpload}
                    disabled={uploadPending}
                    className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {uploadPending ? 'Enviando...' : 'Confirmar Upload'}
                  </button>
                  <button
                    onClick={() => {
                      if (bannerNumber === 1) {
                        setImagePreview1(null);
                        setImageFile1(null);
                        if (fileInputRef1.current) {
                          fileInputRef1.current.value = '';
                        }
                      } else if (bannerNumber === 2) {
                        setImagePreview2(null);
                        setImageFile2(null);
                        if (fileInputRef2.current) {
                          fileInputRef2.current.value = '';
                        }
                      } else {
                        setImagePreview3(null);
                        setImageFile3(null);
                        if (fileInputRef3.current) {
                          fileInputRef3.current.value = '';
                        }
                      }
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Selecionar Imagem
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Dimensões recomendadas: mesma proporção do container "Em breve"
              </p>
            </div>
          )}
        </div>

        {/* Atualizar link do banner existente */}
        {currentBanner && !imagePreview && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link do Banner
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="https://exemplo.com"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                onClick={handleUpdateLink}
                disabled={updateLinkPending}
                className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {updateLinkPending ? 'Atualizando...' : 'Atualizar Link'}
              </button>
            </div>
          </div>
        )}

        {/* Botão de remover */}
        {currentBanner && !imagePreview && (
          <div>
            <button
              onClick={handleDelete}
              disabled={deletePending}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {deletePending ? 'Removendo...' : 'Remover Banner'}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Gerenciar Banners</h2>
        <p className="mt-1 text-sm text-gray-600">
          Os banners substituirão o container "Em breve" na página de workspaces. Você pode adicionar links opcionais aos banners.
        </p>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderBannerSection(
            1,
            currentBanner1,
            imagePreview1,
            imageFile1,
            link1,
            setLink1,
            fileInputRef1,
            handleImageChange1,
            handleUpload1,
            handleUpdateLink1,
            handleDelete1,
            uploadMutation1.isPending,
            updateLinkMutation1.isPending,
            deleteMutation1.isPending
          )}
          {renderBannerSection(
            2,
            currentBanner2,
            imagePreview2,
            imageFile2,
            link2,
            setLink2,
            fileInputRef2,
            handleImageChange2,
            handleUpload2,
            handleUpdateLink2,
            handleDelete2,
            uploadMutation2.isPending,
            updateLinkMutation2.isPending,
            deleteMutation2.isPending
          )}
          {renderBannerSection(
            3,
            currentBanner3,
            imagePreview3,
            imageFile3,
            link3,
            setLink3,
            fileInputRef3,
            handleImageChange3,
            handleUpload3,
            handleUpdateLink3,
            handleDelete3,
            uploadMutation3.isPending,
            updateLinkMutation3.isPending,
            deleteMutation3.isPending
          )}
        </div>
      </div>
    </div>
  );
}

function FeaturesManager() {
  const queryClient = useQueryClient();

  const { data: features, isLoading, refetch } = useQuery({
    queryKey: ['admin-features'],
    queryFn: async () => {
      const response = await api.get('/admin/features');
      console.log('[FeaturesManager] Fetched features:', response.data.data);
      return response.data.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ feature, enabled }: { feature: string; enabled: boolean }) => {
      console.log('[FeaturesManager] Toggling feature:', feature, 'to', enabled);
      const response = await api.post('/admin/features/toggle', { feature, enabled });
      console.log('[FeaturesManager] Response:', response.data);
      return response.data.data;
    },
    onSuccess: async (data) => {
      console.log('[FeaturesManager] Success, new features:', data);
      // Update both queries immediately with the response
      queryClient.setQueryData(['admin-features'], data);
      queryClient.setQueryData(['features'], data);
      // Force immediate refetch
      await refetch();
      await queryClient.refetchQueries({ queryKey: ['features'] });
      alert('Funcionalidade atualizada com sucesso!');
    },
    onError: (error: unknown) => {
      console.error('[FeaturesManager] Erro ao atualizar funcionalidade:', error);
      console.error('[FeaturesManager] Error response:', error);
      alert(getApiErrorMessage(error, 'Erro ao atualizar funcionalidade'));
    },
  });

  if (isLoading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  const featureList = [
    {
      key: 'chat',
      name: 'Chat',
      description: 'Permite aos usuários se comunicarem através de canais e mensagens',
    },
    {
      key: 'community',
      name: 'Comunidade',
      description: 'Permite aos usuários interagirem na comunidade, criar publicações e artigos',
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Gerenciar Funcionalidades</h2>
        <p className="mt-1 text-sm text-gray-600">
          Ative ou desative funcionalidades globalmente para todos os usuários do sistema
        </p>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {featureList.map((feature) => {
            // Check if feature exists in response, default to true if not set
            const featureValue = features?.[feature.key];
            const isEnabled = featureValue === undefined ? true : featureValue === true;
            console.log(`[FeaturesManager] Feature ${feature.key}:`, {
              value: featureValue,
              isEnabled,
              allFeatures: features,
            });
            return (
              <div
                key={feature.key}
                className="border border-gray-200 rounded-lg p-6 flex items-center justify-between"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900">{feature.name}</h3>
                  <p className="mt-1 text-sm text-gray-600">{feature.description}</p>
                  <div className="mt-2">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        isEnabled
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {isEnabled ? 'Ativada' : 'Desativada'}
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => {
                      if (
                        confirm(
                          `Tem certeza que deseja ${isEnabled ? 'desativar' : 'ativar'} a funcionalidade ${feature.name}? ${
                            isEnabled
                              ? 'Ela será desativada para todos os usuários.'
                              : 'Ela será reativada para todos os usuários.'
                          }`
                        )
                      ) {
                        toggleMutation.mutate({
                          feature: feature.key,
                          enabled: !isEnabled,
                        });
                      }
                    }}
                    disabled={toggleMutation.isPending}
                    className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      isEnabled
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {toggleMutation.isPending
                      ? 'Processando...'
                      : isEnabled
                      ? 'Desativar'
                      : 'Ativar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
